import { Box, Typography, Tooltip } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useSelector , useDispatch } from "react-redux";
import {
  getIsTaskSectionExpanded,
  getRunningTimer,
  setTimerRunningInfoToState,
} from "@/store/slices/tasks/tasksSlice";
import moment from "moment";
import { GetFontAwesomeIcon } from "@/components/font-awesome-icon";
import { faStopwatch, faCirclePause } from "@fortawesome/free-solid-svg-icons";
import { useRouter } from "next/router";
import useSignalRClient from "@/services/signal-R/signalr-hubs";
import Task_signalr from "@/services/signal-R/task-signalR-constants";
import guid from "guid";

import styles from "./styles.module.scss";
// import useFloatingTimer from "@/hooks/useFloatingTimer";
import { getLoggedinUser } from "@/store/slices/auth/authSlice";

const {
  hubName,
  InvokeMethodNames: { saveTaskTimer },
} = Task_signalr;

const FloatingTimerWidget = () => {
  const timerRunning = useSelector(getRunningTimer);
  const user = useSelector(getLoggedinUser);
  const isTaskSectionExpanded = useSelector(getIsTaskSectionExpanded);
  const router = useRouter();
  const dispatch = useDispatch();
  const { statuses, RTC } = useSignalRClient({ hubName });
  const taskhub = RTC[hubName];
  const taskhubStatus = statuses[hubName];
  // const { fetchAnyRunningTimer } = useFloatingTimer();

  const [time, setTime] = useState(moment());
  const [position, setPosition] = useState({
    x: 50,
    y: window.innerHeight - 110, // 50px from bottom (60px widget height + 50px margin)
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const widgetRef = useRef(null);
  const intervalRef = useRef(null);
  const isMountedRef = useRef(true);

  // Hide widget only when inline timer is visible (task section expanded on lead page)
  const shouldHideWidget = () => {
    const isOnLeadPage = router.pathname.includes("/leads/");
    if (!isOnLeadPage) return false;

    const currentLeadId = router.query.id ? parseInt(router.query.id) : null;

    return (
      isTaskSectionExpanded &&
      currentLeadId &&
      timerRunning?.leadId === currentLeadId
    );
  };

  // Fetch running timer on mount (for page refresh persistence)
  useEffect(() => {
    if (!user?.Email || timerRunning) return; // Skip if already running or no user
    // fetchAnyRunningTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.Email]);

  // Set isMounted flag for cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset position to default when timer starts
  useEffect(() => {
    if (timerRunning) {
      setPosition({
        x: 50,
        y: window.innerHeight - 110,
      });
    }
  }, [timerRunning]);

  // Position resets to default when timer stops - no localStorage persistence

  // Update timer every second
  useEffect(() => {
    if (timerRunning) {
      intervalRef.current = setInterval(() => {
        if (isMountedRef.current) {
          setTime(moment());
        }
      }, 1000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [timerRunning]);

  // Handle window resize - keep widget within viewport bounds
  useEffect(() => {
    const handleResize = () => {
      if (!widgetRef.current) return;

      const maxX = window.innerWidth - (widgetRef.current?.offsetWidth || 280);
      const maxY = window.innerHeight - (widgetRef.current?.offsetHeight || 60);

      setPosition((prevPosition) => {
        const newX = Math.max(0, Math.min(prevPosition.x, maxX));
        const newY = Math.max(0, Math.min(prevPosition.y, maxY));

        // Only update if position actually changed
        if (newX !== prevPosition.x || newY !== prevPosition.y) {
          return { x: newX, y: newY };
        }
        return prevPosition;
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle mouse down - start dragging
  const handleMouseDown = (e) => {
    if (e.target.closest(`.${styles.pauseButton}`)) {
      return; // Don't start drag when clicking pause button
    }
    if (!widgetRef.current) return; // Guard against null ref
    setIsDragging(true);
    const rect = widgetRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  // Handle mouse move - update position while dragging
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;

      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Constrain to viewport
      const maxX = window.innerWidth - (widgetRef.current?.offsetWidth || 280);
      const maxY = window.innerHeight - (widgetRef.current?.offsetHeight || 60);

      const constrainedX = Math.max(0, Math.min(newX, maxX));
      const constrainedY = Math.max(0, Math.min(newY, maxY));

      setPosition({ x: constrainedX, y: constrainedY });
    };

    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        // Position is temporary - don't save to localStorage
      }
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset, position]);

  // Calculate elapsed time
  const getTimeDifference = (start = moment(), end = moment()) => {
    const duration = moment.duration(end.diff(start));
    const formatted =
      String(Math.floor(duration.asHours())).padStart(2, "0") +
      ":" +
      String(duration.minutes()).padStart(2, "0") +
      ":" +
      String(duration.seconds()).padStart(2, "0");
    return formatted;
  };

  // Handle stop timer
  const handleStopTimer = () => {
    if (!taskhubStatus || !taskhub || !timerRunning) return;

    const payload = {
      taskTimer: {
        leadId: timerRunning?.leadId,
        taskId: timerRunning?.taskId,
        id: timerRunning?.id,
        status: 2,
        startTime: timerRunning?.startTime,
        endTime: moment(),
        duration: getTimeDifference(timerRunning?.startTime),
        type: 1,
      },
      requestId: guid.raw(),
    };

    taskhub
      .invoke(saveTaskTimer, payload)
      .then((res) => {
        if (isMountedRef.current) {
          dispatch(setTimerRunningInfoToState(null));
          console.log("Timer stopped from floating widget", res);
        }
      })
      .catch((error) => {
        console.error("Error stopping timer:", error);
      });
  };

  // Handle widget click - navigate to lead page
  const handleWidgetClick = (e) => {
    // Don't navigate if clicking stop button or if dragging
    if (e.target.closest(`.${styles.pauseButton}`) || isDragging) {
      return;
    }

    // Navigate to lead detail page
    if (timerRunning?.leadId) {
      router.push(`/crm/leads/view/${timerRunning.leadId}`);
    }
  };

  // Don't render if timer is not running or if inline timer is visible
  if (!timerRunning || shouldHideWidget()) {
    return null;
  }

  const elapsedTime = getTimeDifference(timerRunning?.startTime, time);

  return (
    <Box
      ref={widgetRef}
      className={`${styles.floatingWidget} ${isDragging ? styles.dragging : ""}`}
      sx={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => !isDragging && setIsHovered(true)}
      onMouseLeave={() => !isDragging && setIsHovered(false)}
      onClick={handleWidgetClick}
      style={{ cursor: isDragging ? "grabbing" : "pointer" }}
    >
      <Box className={styles.content}>
        <Box className={styles.iconSection}>
          <GetFontAwesomeIcon icon={faStopwatch} />
        </Box>

        <Box className={styles.infoSection}>
          <Tooltip
            title={timerRunning?.leadData?.title || ""}
            arrow
            placement="top"
          >
            <Typography
              className={`${styles.taskTitle} ${isHovered ? styles.visible : styles.hidden}`}
            >
              {timerRunning?.leadData?.title || ""}
            </Typography>
          </Tooltip>
          <Typography className={styles.timerDisplay}>{elapsedTime}</Typography>
        </Box>

        <Box
          className={styles.pauseButton}
          onClick={handleStopTimer}
          title="Stop Timer"
        >
          <GetFontAwesomeIcon icon={faCirclePause} />
        </Box>
      </Box>
    </Box>
  );
};

export default FloatingTimerWidget;
